import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import cn from 'classnames';
import KeyboardArrowUp from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDown from '@material-ui/icons/KeyboardArrowDown';
import './CallTranscript.scss';

import { getEmotionColor } from '../../helpers/emotion_helpers';
import { getTimeDisplayFromNumber } from '../../helpers/formatting';

class CallTranscript extends Component {
  static propTypes = {
    call: PropTypes.object.isRequired,
    audioTime: PropTypes.number.isRequired,
    audioPaused: PropTypes.bool.isRequired,
    setHighlightedEmotion: PropTypes.func.isRequired,
    setHighlightedVariable: PropTypes.func.isRequired,
    setHighlightedParticipant: PropTypes.func.isRequired,
    highlightedEmotion: PropTypes.string,
    highlightedVariable: PropTypes.string,
    highlightedParticipant: PropTypes.object,
  };

  state = {
    searchVal: '',
    lockScroll: false,

    visibleMessageIdOverrides: [],
  };

  componentDidMount() {
    this.outerContainer.addEventListener('scroll', (e) => {
      const isManualScroll = !this.state.lockScroll; // To enable this, set the timeout 750 to over 1000ms
      if (isManualScroll) {
        // console.log('Manual Scroll!');
      } else {
        // console.log('Auto Scroll!');
      }
    });
  }

  componentDidUpdate(prevProps) {
    // Handling highlighting and scroll state management
    if (prevProps.audioTime !== this.props.audioTime) {
      this.handleMsgHighlighting();
    }

    const highlightedEmotionChanged = prevProps.highlightedEmotion !== this.props.highlightedEmotion;
    const highlightedVariableChanged = prevProps.highlightedVariable !== this.props.highlightedVariable;
    const highlightedParticipantChanged = prevProps.highlightedParticipant?.id !== this.props.highlightedParticipant?.id;
    const highlightingChanged = highlightedEmotionChanged || highlightedVariableChanged || highlightedParticipantChanged;
    if (highlightingChanged) {
      this.setState({ visibleMessageIdOverrides: [] });
    }
  }

  handleMsgHighlighting = () => {
    const { audioTime, call } = this.props;
    const { messages, groupedMessages } = call;
    let groupIndexes = [];
    _.forEach(groupedMessages, (group, idx) => {
      if (audioTime >= group[0]?.start_time_seconds && audioTime < group[group.length - 1]?.end_time_seconds) {
        groupIndexes.push(idx);
      }
    });
    const curMsgs = _.filter(messages, (msg) => audioTime >= msg.start_time_seconds && audioTime < msg?.end_time_seconds);

    // Remove existing;
    const curGroupEls = _.filter(groupIndexes.map((idx) => document.querySelector(`#group-${idx}`)));
    const curMsgEls = _.filter(curMsgs.map((msg) => document.querySelector(`#msg-${msg?.start_time_seconds?.toFixed(0)}`)));

    // eslint-disable-next-line
    document.querySelector('.active-group')?.classList.remove('active-group');
    // eslint-disable-next-line
    document.querySelector('.active-message')?.classList.remove('active-message');
    curGroupEls.forEach((el) => el.classList.add('active-group'));
    curMsgEls.forEach((el) => el.classList.add('active-message'));

    const DESIRED_OFFSET = 80;
    const msgTop = curMsgEls[0]?.getBoundingClientRect().top;
    const needsScroll = curMsgEls.length && msgTop !== DESIRED_OFFSET;
    if (needsScroll && !this.state.lockScroll) {
      this.setState({ lockScroll: true });
      this.outerContainer.scrollTo({
        top: _.max([this.outerContainer.scrollTop + msgTop - DESIRED_OFFSET - this.outerContainer.getBoundingClientRect().top, 0]),
        behavior: 'smooth',
      });
      setTimeout(() => this.setState({ lockScroll: false }), 750); // give time to scroll
    }
  };

  isMessageVisible = (msg) => {
    const { highlightedEmotion, highlightedVariable, highlightedParticipant } = this.props;
    const { searchVal, visibleMessageIdOverrides } = this.state;
    const { text, summary } = msg;

    const isVisibleOverride = visibleMessageIdOverrides.includes(msg.id);
    if (isVisibleOverride) return true;

    const emotion = _.get(summary, 'emotion');
    const isHiddenDueToEmotion = highlightedEmotion && highlightedEmotion !== emotion;
    const isHiddenDueToVariable = highlightedVariable && !msg[highlightedVariable];
    const isHiddenDueToParticipant = highlightedParticipant && highlightedParticipant?.id !== msg?.speaker?.id;
    const isHiddenDueToSearch = searchVal && !text.toLowerCase().includes(searchVal.toLowerCase());
    if (isHiddenDueToEmotion || isHiddenDueToSearch || isHiddenDueToVariable || isHiddenDueToParticipant) return null;
    return true;
  };

  allMessageEmotions = () => {
    let emotions = {};
    _.forEach(this.props.call?.messages, (msg) => (emotions[msg.emotion] = true));
    return _.keys(emotions);
  };

  isFilterApplied = () => !!this.props.highlightedEmotion || !!this.props.highlightedVariable || !!this.props.highlightedParticipant;

  render() {
    const { call, highlightedEmotion, highlightedVariable, audioPaused } = this.props;
    const { searchVal, visibleMessageIdOverrides } = this.state;
    const { groupedMessages } = call;

    return (
      <div
        ref={(ref) => (this.outerContainer = ref)}
        className={cn('call-transcript-outer-container inner-panel card', { 'lock-scroll': this.state.lockScroll })}
      >
        <div className='comments-header'>
          <select
            value={highlightedEmotion || 'All Emotions'}
            onChange={(e) => {
              const val = e.target.value;
              this.props.setHighlightedEmotion(val !== 'All Emotions' ? val : null);
            }}
          >
            <option value='All Emotions'>All Emotions</option>
            {this.allMessageEmotions().map((emotion) => (
              <option value={emotion} key={emotion}>
                {emotion.charAt(0).toUpperCase()}
                {emotion.slice(1)}
              </option>
            ))}
          </select>
          <input placeholder='Search Transcript' value={searchVal} onChange={(e) => this.setState({ searchVal: e.target.value })} />
        </div>
        <div className='comments-container'>
          {_.map(groupedMessages, (messages, i) => {
            const isGroupVisibleFn = (msgs) => !!_.find(msgs, (msg) => this.isMessageVisible(msg));

            const NUM_SHOW_PER_MULTI_GROUP = 3;
            const isGroupVisible = isGroupVisibleFn(messages);
            const prevGroup = groupedMessages[i - 1];
            const nextGroup = groupedMessages[i + 1];
            const isPrevGroupVisible = !prevGroup || isGroupVisibleFn(prevGroup);
            const isNextGroupVisible = !nextGroup || isGroupVisibleFn(nextGroup);
            const showGroups = (groups) =>
              this.setState({ visibleMessageIdOverrides: _.uniq([...visibleMessageIdOverrides, ..._.flatten(groups).map((msg) => msg.id)]) });
            const showPrevGroup = () => showGroups([prevGroup]);
            const showNextGroup = () => showGroups([nextGroup]);
            const showPrevGroups = () => showGroups(groupedMessages.slice(i - 1 - NUM_SHOW_PER_MULTI_GROUP, i - 1));
            const showNextGroups = () => showGroups(groupedMessages.slice(i + 1, i + 1 + NUM_SHOW_PER_MULTI_GROUP)); // Show 3 more

            return !isGroupVisible ? null : (
              <Fragment key={i}>
                {!isPrevGroupVisible && (
                  <>
                    <div className='spacer' />
                    <div className='spacer' />
                  </>
                )}
                <div id={`group-${i}`} className={cn('comment-group-container', { paused: audioPaused })}>
                  {!isPrevGroupVisible && (
                    <div onClick={showPrevGroup} className='show-prev show-more'>
                      {/* <span onClick={showPrevGroup}>SHOW PREVIOUS</span> */}
                      <span onClick={showPrevGroup}>
                        <KeyboardArrowUp />
                      </span>
                      <span className='double' onClick={showPrevGroups}>
                        <KeyboardArrowUp />
                        <KeyboardArrowUp />
                      </span>
                    </div>
                  )}
                  <div className='speaker'>
                    {_.get(messages, '0.speaker.name')}
                    {this.isFilterApplied() && (
                      <div className='timestamp'>
                        <span className='bullet'>•</span>
                        {getTimeDisplayFromNumber(messages[0]?.start_time_seconds)}
                      </div>
                    )}
                  </div>

                  <div className='comment'>
                    {messages.map((chatItem, idx) => {
                      const { start_time_seconds, text, summary } = chatItem;
                      const emotion = _.get(summary, 'emotion');
                      const isVisible = this.isMessageVisible(chatItem);
                      const refId = `item-${i}-${chatItem.start_time_seconds}`;
                      const setTime = () => {
                        this.props.setTime(start_time_seconds);
                        window.__ADD_EVENT__('Call - Click Template Message', { call: this.props.call, text });
                      };
                      const hasColor = emotion !== 'neutral' && isVisible;
                      const isHighlighted = highlightedVariable && chatItem[highlightedVariable];
                      const surroundsHighlighted = highlightedVariable && !chatItem[highlightedVariable];
                      return (
                        <span
                          key={refId}
                          id={`msg-${chatItem.start_time_seconds?.toFixed(0)}`}
                          style={{
                            borderColor: hasColor ? getEmotionColor(emotion, 0.3) : 'transparent',
                            backgroundColor: getEmotionColor(emotion, hasColor ? 0.2 : 0.4), // Used for highlighting when playing
                          }}
                          ref={(ref) => (this[refId] = ref)}
                          className={cn('comment-part', `timestamp-${start_time_seconds?.toFixed(0)}`, {
                            highlighted: isHighlighted,
                            'surrounds-highlighted': surroundsHighlighted,
                          })}
                          onClick={setTime}
                        >
                          {text}
                        </span>
                      );
                    })}
                    {!isNextGroupVisible && (
                      <div className='show-next show-more'>
                        {/* <span onClick={showNextGroup}>SHOW NEXT</span> */}
                        <span onClick={showNextGroup}>
                          <KeyboardArrowDown />
                        </span>
                        <span className='double' onClick={showNextGroups}>
                          <KeyboardArrowDown />
                          <KeyboardArrowDown />
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    );
  }
}

export default CallTranscript;
